/**
 * DataSourceDetailsPanel - the Details TAB of a data source, on the canvas.
 *
 * WHAT THIS TAB IS NOW, top to bottom:
 *
 *   KPI strip     documents · size · keywords · formats, one flush row
 *   Description   the prose, and the editor that writes name/type/keywords
 *   Keywords      the actual list, side by side with the formats it accepts
 *
 * NO ACTIVITY ON THE CANVAS. The Phase 5 rebalance put a `RecentActivityCard`
 * at the bottom of this tab while the container rail already carries an
 * "Activity" pill that docks the full Activity pane - two lists of the same
 * audit rows on one screen. The pane is the one that can be read, resized and
 * kept open, so the canvas copy is gone.
 *
 * WHY IT GREW. Everything above except the description used to live in the
 * Context PANE, stacked as five glass cards each wrapping a grid of glass
 * tiles, in a column that starts at 260px. That was the complaint: the context
 * container was full and everything in it was boxed. Weight belongs on the
 * canvas, which is wide; the pane keeps identity chips, two pressable counts
 * and a timeline. See `surface/DataSourceContextPane` for the other half.
 *
 * NO `sections` PROP. It existed to let this component render either half of
 * that split - 'all' standalone, 'main' on the surface - and it is gone with
 * the split. There is one data-source detail view and this is it.
 *
 * NO BOXES IN BOXES. Sections are `GlassCard`s and their contents are `Field`s
 * and chips, not `FactTile`s: a `FactTile` is itself a glass card, so a grid of
 * them inside a card is the nesting this page was rebalanced to remove. The one
 * flush strip (`padding="none"` + `divide-x`) is the canonical KPI recipe.
 */

import React, { useState, useCallback } from "react";
import { formatRelativeTime } from "../../shared/feed/feedTime";
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import Badge from "../../shared/TailwindComponents/Badge";
import Button from "../../shared/TailwindComponents/Button";
import FieldShell from "../../shared/TailwindComponents/FieldShell";
import Input from "../../shared/TailwindComponents/Input";
import Skeleton from "../../shared/TailwindComponents/Skeleton";
import Textarea from "../../shared/TailwindComponents/Textarea";
import {
  PiTextAlignLeftLight,
  PiTagLight,
  PiFileLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiPencilLight,
  PiXLight,
  PiPlusLight,
  PiClockLight,
} from "react-icons/pi";

/* ─── Shared types (exported for consumers) ─── */

export type AccessLevel = "view" | "edit" | "admin";

export type ShareUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  initials: string;
  avatarUrl: string;
  access?: AccessLevel;
};

export type DataSource = {
  id: string;
  uuid?: string;
  /* Supports both API (snake_case) and legacy (PascalCase) field names */
  DataSourceName?: string;
  name?: string;
  DataSourceType?: "Personal" | "Shared" | "Company";
  visibility?: string;
  last_index_run?: string;
  CreatedAt?: string;
  created_at?: string;
  updated_at?: string;
  document_count?: number;
  total_storage_size?: string;
  total_size_bytes?: number;
  language?: string;
  description?: string;
  type?: string;
  status?: string;
  indexing_status?: string;
  indexer_status?: string;
  keywords?: string[];
  shared_with?: ShareUser[];
  collaborators?: any[];
  allowed_file_types?: string[] | string;
  last_indexed_at?: string | null;
  last_synced_at?: string | null;
  llm_generated_at?: string | null;
};

export type DataSourceDetailsProps = {
  loading?: boolean;
  dataSource?: DataSource;
  canEdit?: boolean;
  /*
    NO SHARING PROPS. `canShare`, `onShareUpdate` and `onRevokeAccess` were
    declared, destructured and threaded from the page - and never read by a
    single line of this component's body. The page's sharing UI was the
    header menu; it is the Access pane now (see `surface/layout`), and
    removal goes through the shared manage side panel.
  */
  onSave?: (updates: { name?: string; description?: string; type?: string; keywords?: string[] }) => Promise<boolean>;
  t?: (key: string) => string;
  /*
    NO `onOpenActivity`. It existed for the canvas activity card's "view all"
    link. Activity lives in the rail pane now, which the container opens
    itself, so the tab has nothing to hand off.
  */
};

/* ─── Helpers ───
   Exported because the Context pane renders the same facts against the same
   vocabulary; two copies of "what does indexer_status = transientFailure
   mean" is how the two views start disagreeing. */

export const formatSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  let size = bytes;
  do {
    size /= 1024;
    i++;
  } while (size >= 1024 && i < units.length - 1);
  return `${size.toFixed(1)} ${units[i]}`;
};

export const visibilityLabel = (ds?: DataSource) => {
  if (ds?.DataSourceType) return ds.DataSourceType;
  const v = ds?.visibility;
  if (v === "private") return "Personal";
  if (v === "organization") return "Company";
  if (v === "shared") return "Shared";
  return v || "—";
};

export type StatusCfg = { label: string; badge: string };
export const statusCfg = (s?: string): StatusCfg => {
  const m: Record<string, StatusCfg> = {
    active:           { label: "Active",           badge: "green" },
    ready:            { label: "Ready",            badge: "green" },
    indexing:         { label: "Processing",        badge: "blue" },
    pending:          { label: "Pending",           badge: "yellow" },
    pending_indexing: { label: "Pending Processing", badge: "yellow" },
    queued:           { label: "Queued",            badge: "yellow" },
    error:            { label: "Error",             badge: "red" },
    failed:           { label: "Failed",            badge: "red" },
    archived:         { label: "Archived",          badge: "gray" },
  };
  return m[s || ""] || { label: s || "Unknown", badge: "gray" };
};

export const indexerCfg = (s?: string): { label: string; badge: string } => {
  const m: Record<string, { label: string; badge: string }> = {
    completed:        { label: "Completed",   badge: "green" },
    success:          { label: "Success",     badge: "green" },
    running:          { label: "Running",     badge: "blue" },
    transientFailure: { label: "Retrying",    badge: "yellow" },
    failed:           { label: "Failed",      badge: "red" },
    error:            { label: "Error",       badge: "red" },
    reset:            { label: "Reset",       badge: "purple" },
    notStarted:       { label: "Not Started", badge: "gray" },
  };
  return m[s || ""] || { label: s || "Not Started", badge: "gray" };
};

export const parseFileTypes = (ft?: string[] | string): string[] => {
  if (!ft) return [];
  if (Array.isArray(ft)) return ft;
  return ft.split(",").map((s) => s.trim()).filter(Boolean);
};

/* ─── Main Panel ─── */

const DataSourceDetailsPanel: React.FC<DataSourceDetailsProps> = ({
  loading,
  dataSource: ds,
  canEdit = true,
  onSave,
  t = (s) => s,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editType, setEditType] = useState("");
  const [editKeywords, setEditKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState("");

  const startEditing = useCallback(() => {
    setEditName(ds?.DataSourceName || ds?.name || "");
    setEditDescription(ds?.description || "");
    setEditType(ds?.type || "");
    setEditKeywords([...(ds?.keywords || [])]);
    setNewKeyword("");
    setIsEditing(true);
  }, [ds]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setNewKeyword("");
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      const updates: { name?: string; description?: string; type?: string; keywords?: string[] } = {};
      const currentName = ds?.DataSourceName || ds?.name || "";
      if (editName !== currentName) updates.name = editName;
      if (editDescription !== (ds?.description || "")) updates.description = editDescription;
      if (editType !== (ds?.type || "")) updates.type = editType;
      if (JSON.stringify(editKeywords) !== JSON.stringify(ds?.keywords || [])) updates.keywords = editKeywords;

      if (Object.keys(updates).length > 0) {
        const ok = await onSave(updates);
        if (ok) setIsEditing(false);
      } else {
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave, editName, editDescription, editType, editKeywords, ds]);

  const addKeyword = useCallback(() => {
    const kw = newKeyword.trim();
    if (kw && !editKeywords.includes(kw)) {
      setEditKeywords((prev) => [...prev, kw]);
      setNewKeyword("");
    }
  }, [newKeyword, editKeywords]);

  const removeKeyword = useCallback((kw: string) => {
    setEditKeywords((prev) => prev.filter((k) => k !== kw));
  }, []);

  /* ── Loading ──
     THE shared skeleton, at the shape of this page. The hand-rolled grid of
     `bg-gray-200 dark:bg-gray-700 animate-fadePulse` blocks that used to sit
     here was a second, drifting copy of `<Skeleton variant="detail" />`. */
  if (loading) {
    return (
      <div className="w-full h-full overflow-y-auto custom-scrollbar-main">
        <Skeleton variant="detail" />
      </div>
    );
  }

  const fileTypes = parseFileTypes(ds?.allowed_file_types);
  const keywords = ds?.keywords || [];

  return (
    <div className="details-canvas w-full h-full overflow-y-auto custom-scrollbar-main">
      <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-8">

        {/* ── KPI strip ──
            One flush row of numbers, the canonical `padding="none"` + divided
            grid. These were four separate glass tiles in a 260px pane. */}
        <GlassCard padding="none" data-tour-id="ds-details-metadata">
          <div className="details-kpis divide-hairline-soft">
            <KpiTile
              icon={<PiFileLight />}
              value={ds?.document_count ?? 0}
              label={t("Documents")}
            />
            <KpiTile
              icon={<PiDatabaseLight />}
              value={formatSize(ds?.total_size_bytes)}
              label={t("Total size")}
            />
            <KpiTile
              icon={<PiTagLight />}
              value={keywords.length}
              label={t("Keywords")}
            />
            {/* Was "Supported formats" - the same platform-wide number on
                every source. When it last changed is about THIS one. */}
            <KpiTile
              icon={<PiClockLight />}
              value={(ds?.last_indexed_at || ds?.updated_at)
                ? formatRelativeTime((ds?.last_indexed_at || ds?.updated_at) as string, t as any)
                : '—'}
              label={t("Last updated")}
            />
          </div>
        </GlassCard>

        {/* ── Description, and the editor for the whole record ── */}
        <GlassCard
          data-tour-id="ds-details-description"
          icon={<PiTextAlignLeftLight />}
          title={t("Description")}
          padding="lg"
          actions={
            canEdit && !isEditing ? (
              <Button
                variant="primary"
                size="small"
                onClick={startEditing}
                icon={<PiPencilLight className="h-3.5 w-3.5" />}
              >
                {t("Edit")}
              </Button>
            ) : undefined
          }
        >
          {isEditing && canEdit ? (
            <div className="space-y-4">
              <Input
                type="text"
                label={t("Data source name")}
                placeholder={t("e.g., Employment Law Library")}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full"
              />
              <Textarea
                label={t("Description")}
                placeholder={t("Describe what this data source contains...")}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full"
                id="ds-description"
              />
              <Input
                type="text"
                label={t("Type / Category")}
                placeholder={t("e.g. Legal Documents, Case Law, Contracts")}
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
                className="w-full"
              />

              {/* Keyword editor */}
              <FieldShell id="ds-keyword-input" label={t("Keywords")} translate={false}>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editKeywords.map((kw, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-info-50 dark:bg-info-900/30 text-xs font-medium text-info-700 dark:text-info-300 border border-info-200 dark:border-info-700 capitalize"
                    >
                      {kw}
                      <button
                        type="button"
                        onClick={() => removeKeyword(kw)}
                        className="text-info-400 hover:text-info-600 dark:hover:text-info-200"
                      >
                        <PiXLight className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 items-end">
                  <Input
                    id="ds-keyword-input"
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                    placeholder={t("Add a keyword...")}
                    className="flex-1"
                  />
                  <Button
                    variant="primary"
                    size="small"
                    onClick={addKeyword}
                    disabled={!newKeyword.trim()}
                    icon={<PiPlusLight className="h-4 w-4" />}
                  >
                    {t("Add")}
                  </Button>
                </div>
              </FieldShell>

              <div className="flex justify-end gap-3 pt-3 border-t border-hairline-soft">
                <Button variant="neutral" size="small" onClick={cancelEditing} disabled={isSaving}>
                  {t("Cancel")}
                </Button>
                <Button variant="primary" size="small" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? t("Saving...") : t("Save changes")}
                </Button>
              </div>
            </div>
          ) : ds?.description ? (
            <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap max-w-prose">
              {ds.description}
            </p>
          ) : (
            <p className="text-sm text-fg-muted">
              {t("No description available. A description will be auto-generated after processing completes.")}
            </p>
          )}
        </GlassCard>

        {/* ── Vocabulary: keywords beside the formats they are matched in ──
            Two cards on one row, each holding a plain chip run. In the pane
            these were two more stacked cards nobody could read at 260px. */}
        <div data-tour-id="ds-details-sidebar" className="details-grid-2 gap-5">
          <GlassCard
            icon={<PiTagLight />}
            title={t("Keywords")}
            subtitle={t("Improve search relevance across the workspace")}
            padding="lg"
          >
            {keywords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, i) => (
                  <Badge key={i} color="blue" className="capitalize">
                    {kw}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-fg-muted">
                {t("No keywords yet. Click Edit to add keywords, or they will be auto-generated after processing.")}
              </p>
            )}
          </GlassCard>

          <GlassCard icon={<PiFileTextLight />} title={t("Supported formats")} padding="lg">
            {fileTypes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fileTypes.map((ft, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2.5 py-1 rounded-lg bg-surface-sunken
                               text-xs font-mono font-medium text-fg-muted border border-hairline-soft"
                  >
                    {ft}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-fg-muted">
                {t("Default file types: .pdf, .docx, .txt, .md")}
              </p>
            )}
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default DataSourceDetailsPanel;
